import { DragEvent, FunctionComponent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { faXmark } from "@fortawesome/pro-regular-svg-icons";

import { classNames } from "@/utils/classNames";
import { Icon } from "@/components";

import { ClearChipsButton } from "./ClearChipsButton";
import { Tier } from "./criteriaTiers";
import { SuggestionMenu } from "./SuggestionMenu";
import {
	decodeTierChipDrag,
	encodeTierChipDrag,
	TIER_CHIP_MEDIA_TYPE,
	TierChipDrag,
} from "./tierDragDrop";
import { useChipSuggestionKeys } from "./useChipSuggestionKeys";
import { spellingWithin, useFilterPropertySuggestions } from "./useFilterPropertySuggestions";

/** Which taxonomy backs the suggestions — the same lists the rubric editor uses. */
export type TaxonomyKind = "country" | "industry";

const PROPERTY_NAME: Record<TaxonomyKind, string> = {
	country: "generalCountry",
	industry: "industry",
};

interface TaxonomyChipInputProps {
	kind: TaxonomyKind;
	values: string[];
	placeholder: string;
	/** Colours the chips like the tier they belong to. */
	chipClassName: string;
	name: string;
	/**
	 * Values the rubric understands that the taxonomy does not list — today only
	 * the "Rest of the world" catch-all. Without them a user who removes the chip
	 * has no way to put it back, because nothing in `generalCountry` matches it.
	 */
	extraOptions?: string[];
	/**
	 * Whether a value the taxonomy does not list may be added anyway. Industries
	 * and locations are both read by the scoring model as written ("Law Practice"
	 * scores as "Legal Services", "Asia (excl. China)" is a place, PREC-4515), so
	 * anything the user types works. The API keeps only the countries its tables
	 * know when it builds search filters from the rubric.
	 */
	allowFreeText?: boolean;
	/**
	 * Rubric key of the criterion this row belongs to. Set it to make the chips
	 * draggable between the criterion's buckets; a chip from another criterion is
	 * refused, so a country cannot be dropped into the industry card.
	 */
	dragGroup?: string;
	/** The bucket this row edits — travels with a dragged chip. */
	tier?: Tier;
	/** A chip from `dragGroup` was dropped here; the payload says where from. */
	onMoveHere?: (payload: TierChipDrag) => void;
	onChange: (values: string[]) => void;
}

/**
 * Chip input backed by the scoring taxonomy: the user picks from what the
 * enrichment can actually report (the same `list/filterByProperty` lists the
 * Lead Scoring rubric editor uses), by click or by keyboard.
 */
export const TaxonomyChipInput: FunctionComponent<TaxonomyChipInputProps> = ({
	kind,
	values,
	placeholder,
	chipClassName,
	name,
	extraOptions,
	allowFreeText = false,
	dragGroup,
	tier,
	onMoveHere,
	onChange,
}) => {
	const { t } = useTranslation();
	const boxRef = useRef<HTMLDivElement>(null);
	const [isDropTarget, setIsDropTarget] = useState(false);

	const { searchValue, setSearchValue, options, reset, spellingOf, hasAnswered } =
		useFilterPropertySuggestions({
			propertyName: PROPERTY_NAME[kind],
			selected: values,
			extraOptions,
		});

	// The row as last rendered: a value added once its spelling arrived must not write back a
	// stale row or call a stale handler.
	const latest = useRef({ values, onChange });

	latest.current = { values, onChange };

	const addToRow = (entry: string) => {
		const { values: row, onChange: emit } = latest.current;

		if (!row.some((existing) => existing.toLowerCase() === entry.toLowerCase())) {
			emit([...row, entry]);
		}
	};

	// A value typed in another case is taken in the list's spelling, and never added beside the same
	// value already in the row ("germany" next to "Germany"), on Enter, a click or a blur (PREC-4515).
	// Taken before the suggestions answered, the list is asked first — briefly, so a slow answer still
	// adds the value as typed — and the value is added once: a respell afterwards missed the change
	// the editor holds behind its "Activate the criterion" dialog (PREC-4515 QA).
	const select = (value: string) => {
		const trimmed = value.trim();
		const typed = trimmed.toLowerCase();
		const isInRow = values.some((existing) => existing.toLowerCase() === typed);
		const listed = options.find((option) => option.toLowerCase() === typed);

		reset();

		if (!trimmed || isInRow) {
			return;
		}

		if (listed || !allowFreeText || hasAnswered(trimmed)) {
			addToRow(listed ?? trimmed);

			return;
		}

		void spellingWithin(spellingOf, trimmed).then(addToRow);
	};

	/**
	 * The typed value, offered as an option of its own once the taxonomy has
	 * nothing by that name. Without it "you may type your own industry" was only
	 * discoverable by pressing Enter into an empty menu and hoping.
	 */
	const customValue = (() => {
		const typed = searchValue.trim();

		if (!allowFreeText || !typed) {
			return undefined;
		}

		const isKnown = [...options, ...values].some(
			(entry) => entry.toLowerCase() === typed.toLowerCase()
		);

		return isKnown ? undefined : typed;
	})();

	// Last, not first: the taxonomy's own matches are the better answer, and the
	// arrow keys should reach them before the user's raw text.
	const offered = customValue ? [...options, customValue] : options;

	const { suggestions, highlighted, handleKeyDown, clearHighlight } = useChipSuggestionKeys({
		allowFreeText,
		draft: searchValue,
		onAdd: select,
		onChange,
		options: offered,
		values,
	});

	const isDraggable = dragGroup !== undefined && tier !== undefined;

	const startDrag = (event: DragEvent<HTMLElement>, value: string) => {
		if (!isDraggable) {
			return;
		}

		event.dataTransfer.effectAllowed = "move";
		event.dataTransfer.setData(
			TIER_CHIP_MEDIA_TYPE,
			encodeTierChipDrag({ criterion: dragGroup, tier, value })
		);
	};

	// Only a chip of this very criterion may land here — `dragover` can read the
	// media types but never the payload, so the private type is the whole check.
	const allowDrop = (event: DragEvent<HTMLDivElement>) => {
		if (!isDraggable || !event.dataTransfer.types.includes(TIER_CHIP_MEDIA_TYPE)) {
			return;
		}

		event.preventDefault();
		event.dataTransfer.dropEffect = "move";
		setIsDropTarget(true);
	};

	const drop = (event: DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setIsDropTarget(false);

		const payload = decodeTierChipDrag(event.dataTransfer.getData(TIER_CHIP_MEDIA_TYPE));

		if (payload && payload.criterion === dragGroup) {
			onMoveHere?.(payload);
		}
	};

	return (
		<div className="min-w-0 flex-1">
			<div
				ref={boxRef}
				className={classNames(
					"input-styles flex h-auto min-h-[38px] flex-wrap items-center gap-1.5 rounded-lg border bg-white px-2 py-1.5",
					isDropTarget && "border-brand-light bg-blue-action-200/30"
				)}
				onDragEnter={allowDrop}
				onDragLeave={() => setIsDropTarget(false)}
				onDragOver={allowDrop}
				onDrop={drop}
			>
				{values.map((value) => (
					<span
						key={value}
						className={classNames(
							"flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold",
							isDraggable && "cursor-grab active:cursor-grabbing",
							chipClassName
						)}
						draggable={isDraggable}
						onDragEnd={() => setIsDropTarget(false)}
						onDragStart={(event) => startDrag(event, value)}
					>
						{value}
						<button
							aria-label={t("onboardingTour.scoringStep.segments.removeChip", { label: value })}
							className="cursor-pointer"
							type="button"
							onClick={() => onChange(values.filter((entry) => entry !== value))}
						>
							<Icon className="h-3 w-3" icon={faXmark} />
						</button>
					</span>
				))}
				<input
					aria-label={placeholder}
					autoComplete="off"
					className="min-w-[120px] flex-1 border-0 bg-transparent text-sm outline-none placeholder:text-text-meta"
					name={name}
					placeholder={placeholder}
					type="text"
					value={searchValue}
					onBlur={() => allowFreeText && select(searchValue)}
					onChange={(event) => {
						setSearchValue(event.target.value);
						clearHighlight();
					}}
					onKeyDown={handleKeyDown}
				/>
				<ClearChipsButton visible={values.length > 0} onClear={() => onChange([])} />
			</div>

			<SuggestionMenu
				activeIndex={highlighted}
				anchorRef={boxRef}
				customValue={customValue}
				options={suggestions}
				onSelect={select}
			/>
		</div>
	);
};
